import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // user states: null = checking, false = unauthenticated, object = authenticated
  const [user, setUser] = useState(null);

  const fetchMe = useCallback(async () => {
    try {
      // /auth/me always returns 200 — anonymous responses come back as
      // {authenticated: false}. This keeps the browser console free of
      // 401-shaped "Failed to load resource" noise on every page load.
      const res = await api.get("/auth/me");
      if (!res.data || res.data.authenticated === false) {
        setUser(false);
        return false;
      }
      setUser(res.data);
      return res.data;
    } catch (err) {
      // Genuine transport / 5xx / network failure.
      if (typeof console !== "undefined") console.debug("[auth] /auth/me transport error:", err?.response?.status || err?.message);
      setUser(false);
      return false;
    }
    // `api` and `setUser` are stable module-level references; empty deps
    // are intentional. This effect runs once on mount via useEffect below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      return { ok: true, user: data };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const register = async (name, email, password) => {
    try {
      const { data } = await api.post("/auth/register", { name, email, password });
      setUser(data);
      return { ok: true, user: data };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      // Logout POST can fail if the session is already invalid on the server.
      // We proceed with client-side logout regardless — the user's intent is
      // clear — but we log so this shows up in error monitoring.
      if (typeof console !== "undefined") console.warn("[auth] logout POST failed:", err);
    }
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, login, register, logout, refresh: fetchMe }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
